/**
 * Batch roll-up for the Cockpit batch progress band (#113).
 *
 * Groups CSV-sourced appointment rows by upload batch and aggregates their
 * live eligibility statuses into the four counts the band renders:
 *
 *   - answered    — a check finished with an answer (eligible / ineligible /
 *                   needs review / stale). The band's green "checked" count.
 *   - inProgress  — check currently running.
 *   - queued      — no check dispatched yet (the sweep hasn't picked it up).
 *   - cantCheck   — no answer is coming without human action: data_issue
 *                   ("Cannot check" — never sent) or error (system/payer
 *                   failure past retries).
 *
 * A batch is `finished` when nothing is queued or running — every row reached
 * some terminal state. `clear` (rows with nothing to do, per the same rollup
 * product decision the glance colors use: only `eligible` is green) feeds the
 * finished band's "X clear · Y need review" line.
 *
 * Batch identity: all rows of one upload share the exact (csvFileName,
 * csvUploadedAt) pair — `submitCsvAppointments` stamps one `nowIso` per call.
 */
import type { AppointmentPatient } from '../types'

export interface BatchRollup {
  /** Stable identity: `${csvUploadedAt}|${csvFileName}`. */
  key: string
  fileName: string
  /** ISO 8601 upload timestamp (identical across the batch's rows). */
  uploadedAt: string
  clinicId: string | null
  total: number
  answered: number
  inProgress: number
  queued: number
  cantCheck: number
  /** Answered rows with nothing left to do (status `eligible`). */
  clear: number
  /** Answered rows a human should look at (ineligible / needs review / stale). */
  needsReview: number
  finished: boolean
}

export function batchKeyOf(p: AppointmentPatient): string | null {
  if (p.source !== 'csv' || !p.csvUploadedAt || !p.csvFileName) return null
  return `${p.csvUploadedAt}|${p.csvFileName}`
}

/** How long an upload stays on the band after it finishes (or stalls). */
export const BATCH_WINDOW_MS = 24 * 60 * 60 * 1000

/** Cadence of the Cockpit's shared clock while a band is live (useNow). */
export const BATCH_CLOCK_RESOLUTION_MS = 30_000

/** Is this upload timestamp still inside the band window at `now`? The ONE
 * window test — computeBatchRollups (does the band render?) and
 * hasLiveCsvBatch (should the clock run?) both go through it, so the two can
 * never disagree on the constant or the comparison. */
export function isWithinBatchWindow(
  uploadedAt: string,
  now: number,
  windowMs: number = BATCH_WINDOW_MS,
): boolean {
  const uploadedMs = Date.parse(uploadedAt)
  return !Number.isNaN(uploadedMs) && now - uploadedMs <= windowMs
}

/**
 * Should the Cockpit's 30s band clock be running? True while any CSV row's
 * batch could still render a band — i.e. its upload is inside the 24h window,
 * padded by one clock step. The padding breaks the circularity of "window
 * membership depends on the clock the subscription gates": the rollup ages a
 * band out using the ROUNDED tick (up to BATCH_CLOCK_RESOLUTION_MS behind
 * wall time), so an unpadded wall-clock test could unsubscribe one render
 * BEFORE the rollup drops the band, freezing it on screen with no tick left
 * to remove it. Padded, the clock always outlives the band by at most one
 * tick, and the expiry tick itself re-renders the band away.
 */
export function hasLiveCsvBatch(patients: AppointmentPatient[], now: number): boolean {
  return patients.some(
    p =>
      batchKeyOf(p) !== null &&
      isWithinBatchWindow(p.csvUploadedAt!, now, BATCH_WINDOW_MS + BATCH_CLOCK_RESOLUTION_MS),
  )
}

/**
 * The one timestamp the band pipeline ages against in a given render.
 * Invariant: "clock off" must always imply "no band renders".
 *
 * While the clock is subscribed (`clockOn`), the rounded useNow tick drives
 * both the roll-ups and the re-renders — unchanged. While the clock is OFF,
 * `useNow(null)` returns the store's parked last-known time, which can be
 * hours stale after a remount; aging the roll-ups with it can resurrect a
 * band the gate already judged expired, with no subscription left to ever
 * tick it away (the batch crossed 24h while the Cockpit was unmounted).
 * So when the clock is off we age with the SAME wall reading the gate used,
 * rounded down to the clock resolution so the value is stable across renders
 * within one tick (memos hold). Rounding is safe: it under-ages by less than
 * one resolution, and hasLiveCsvBatch pads the window by exactly one
 * resolution — any upload old enough to leave the clock off is still outside
 * the un-padded window at the rounded reading.
 */
export function selectBandNow(
  clockOn: boolean,
  tick: number,
  wallNow: number,
  resolutionMs: number = BATCH_CLOCK_RESOLUTION_MS,
): number {
  if (clockOn) return tick
  return Math.floor(wallNow / resolutionMs) * resolutionMs
}

/**
 * Group + aggregate CSV rows into per-batch roll-ups, newest upload first.
 * Only batches uploaded within `windowMs` of `now` are returned — older ones
 * age off the band even if never dismissed.
 */
export function computeBatchRollups(
  patients: AppointmentPatient[],
  now: number,
  windowMs: number = BATCH_WINDOW_MS,
): BatchRollup[] {
  const byKey = new Map<string, BatchRollup>()

  for (const p of patients) {
    const key = batchKeyOf(p)
    if (!key) continue
    if (!isWithinBatchWindow(p.csvUploadedAt!, now, windowMs)) continue

    let b = byKey.get(key)
    if (!b) {
      b = {
        key,
        fileName: p.csvFileName!,
        uploadedAt: p.csvUploadedAt!,
        clinicId: p.clinicId ?? null,
        total: 0, answered: 0, inProgress: 0, queued: 0, cantCheck: 0,
        clear: 0, needsReview: 0, finished: false,
      }
      byKey.set(key, b)
    }

    b.total++
    const s = p.eligibilityStatus ?? 'not_checked'
    switch (s) {
      case 'in_progress':
        b.inProgress++
        break
      case 'not_checked':
        b.queued++
        break
      case 'data_issue':
      case 'error':
        b.cantCheck++
        break
      case 'eligible':
        b.answered++
        b.clear++
        break
      default: // not_eligible, needs_review, stale — answered, but act on it
        b.answered++
        b.needsReview++
        break
    }
  }

  const out = [...byKey.values()]
  for (const b of out) b.finished = b.inProgress === 0 && b.queued === 0
  // Newest upload first (ISO strings compare lexicographically).
  out.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt))
  return out
}
